import { computed, ref, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { CallService } from '@/services/telehealthApi/CallService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useConsultationRequestStore } from './consultationRequest'
import {
  VideoProvider,
  type ConsultationRequestDTO,
  type ConsultationRequestWithCaseDTO,
  type JoinConsultationRequestWithShareCode200Response,
  type JoinScheduledConsultationRequestWithShareCode200Response,
  type ScheduledConsultationDTO
} from '@/generated'

const ACTIVE_CALL_ID_KEY = 'active_call_id'
const ACTIVE_CALL_TYPE_KEY = 'active_call_type'

type CallType = 'direct' | 'scheduled'

interface BaseCallAccess {
  consultationRequestId: string
  type: CallType
}

export interface AkCallAccess extends BaseCallAccess {
  provider: typeof VideoProvider.AK
  roomId: number
  accessToken: string
}

export interface WebexCallAccess extends BaseCallAccess {
  provider: typeof VideoProvider.WEBEX
  meetingId: string
  webLink: string
  guestToken: string
  password: string
}

export type CallAccess = AkCallAccess | WebexCallAccess

export interface JoinCallOptions {
  showJoiningLoader?: boolean
}

const UNSUPPORTED_PROVIDER_MESSAGE = 'This call uses a video provider that is not supported yet.'

class UnsupportedCallProviderError extends Error {
  constructor(provider: string) {
    super(`Unsupported call provider: ${provider}`)
  }
}

function toScheduledCallAccess(
  response: JoinScheduledConsultationRequestWithShareCode200Response
): CallAccess {
  switch (response.provider) {
    case VideoProvider.AK:
      return {
        provider: VideoProvider.AK,
        roomId: response.roomId,
        accessToken: response.accessToken,
        consultationRequestId: response.scheduledConsultationRequestId,
        type: 'scheduled'
      }
    case VideoProvider.WEBEX:
      return {
        provider: VideoProvider.WEBEX,
        meetingId: response.meetingId,
        webLink: response.webLink,
        guestToken: response.guestToken,
        password: response.password,
        consultationRequestId: response.scheduledConsultationRequestId,
        type: 'scheduled'
      }
    default:
      throw new UnsupportedCallProviderError((response as { provider: string }).provider)
  }
}

function toDirectCallAccess(
  response: JoinConsultationRequestWithShareCode200Response,
  consultationRequestId: string
): CallAccess {
  switch (response.provider) {
    case VideoProvider.AK:
      return {
        provider: VideoProvider.AK,
        roomId: response.roomId,
        accessToken: response.accessToken,
        consultationRequestId,
        type: 'direct'
      }
    case VideoProvider.WEBEX:
      return {
        provider: VideoProvider.WEBEX,
        meetingId: response.meetingId,
        webLink: response.webLink,
        guestToken: response.guestToken,
        password: response.password,
        consultationRequestId,
        type: 'direct'
      }
    default:
      throw new UnsupportedCallProviderError((response as { provider: string }).provider)
  }
}

export const useCallStore = defineStore('call', () => {
  const noticeStore = useNoticeStore()
  const {
    refreshIncomingConsultationRequestList,
    refreshOutgoingConsultationRequestList,
    refreshScheduledConsultationById,
    getIncomingDirectConsultationById,
    getIncomingScheduledConsultationById,
    getOutgoingDirectConsultationById,
    getOutgoingScheduledConsultationById,
    getDirectConsultationById
  } = useConsultationRequestStore()

  const callAccess: Ref<CallAccess | undefined> = ref()
  const consultationShareCode = ref<string | undefined>()
  const scheduledConsultationShareCode = ref<string | undefined>()
  const directConsultationRequest = ref<
    ConsultationRequestDTO | ConsultationRequestWithCaseDTO | undefined
  >()
  const scheduledConsultation = ref<ScheduledConsultationDTO | null>()
  const isJoiningCall = ref(false)

  const isInCall = computed<boolean>(() => !!callAccess.value)
  const callProvider = computed<VideoProvider | undefined>(() => callAccess.value?.provider)
  const isAkCall = computed<boolean>(() => callProvider.value === VideoProvider.AK)
  const isWebexCall = computed<boolean>(() => callProvider.value === VideoProvider.WEBEX)
  const akCallAccess = computed<AkCallAccess | undefined>(() =>
    callAccess.value?.provider === VideoProvider.AK ? callAccess.value : undefined
  )
  const webexCallAccess = computed<WebexCallAccess | undefined>(() =>
    callAccess.value?.provider === VideoProvider.WEBEX ? callAccess.value : undefined
  )
  const roomId = computed<number | undefined>(() => akCallAccess.value?.roomId)
  const accessToken = computed<string | undefined>(() => akCallAccess.value?.accessToken)
  const callConsultationRequestId = computed<string | undefined>(
    () => callAccess.value?.consultationRequestId
  )
  const isInDirectCall = computed<boolean>(() => callAccess.value?.type === 'direct')
  const isInScheduledCall = computed<boolean>(() => callAccess.value?.type === 'scheduled')

  async function startCall(consultationRequestId: string): Promise<void> {
    isJoiningCall.value = true
    try {
      const callAccessResponse = await CallService.joinCall(consultationRequestId)
      const access = toDirectCallAccess(callAccessResponse, consultationRequestId)
      directConsultationRequest.value = resolveDirectConsultation(
        callAccessResponse.consultationRequestId
      )

      callAccess.value = access
      persistActiveCallId(consultationRequestId, 'direct')
    } catch (err) {
      notifyCallError(err, 'Failed to start the call')
    } finally {
      isJoiningCall.value = false
    }
  }

  async function getConsultationShareCode(consultationRequestId: string): Promise<void> {
    try {
      const _consultationShareCode = (
        await CallService.getConsultationShareCode(consultationRequestId)
      ).consultationShareCode

      consultationShareCode.value = `https://${window.location.host}/${_consultationShareCode}`
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to get the share code'
      })
    }
  }

  async function getScheduledConsultationShareCode(consultationRequestId: string): Promise<void> {
    try {
      const _sharedConsultationShareCode = (
        await CallService.getScheduledConsultationShareCode(consultationRequestId)
      ).consultationShareCode

      scheduledConsultationShareCode.value = `https://${window.location.host}/consultation-details/${consultationRequestId}?shareCode=${_sharedConsultationShareCode}`
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to get the share code'
      })
    }
  }

  async function joinCall(
    consultationRequestId: string,
    { showJoiningLoader = true }: JoinCallOptions = {}
  ): Promise<void> {
    isJoiningCall.value = showJoiningLoader
    try {
      await Promise.all([
        refreshOutgoingConsultationRequestList(),
        refreshIncomingConsultationRequestList()
      ])

      const callAccessResponse = await CallService.joinCall(consultationRequestId)
      const access = toDirectCallAccess(callAccessResponse, consultationRequestId)
      directConsultationRequest.value = resolveDirectConsultation(
        callAccessResponse.consultationRequestId
      )

      callAccess.value = access
      persistActiveCallId(consultationRequestId, 'direct')
    } catch (err) {
      notifyCallError(err, 'Failed to join the call')
    } finally {
      isJoiningCall.value = false
    }
  }

  function isShareCodeAnUuid(shareCode: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      shareCode
    )
  }

  async function joinCallWithConsultationShareCode(consultationShareCode: string): Promise<void> {
    isJoiningCall.value = true
    try {
      if (!isShareCodeAnUuid(consultationShareCode)) {
        return
      }
      const callAccessResponse =
        await CallService.joinWithConsultationShareCode(consultationShareCode)
      const access = toDirectCallAccess(
        callAccessResponse,
        callAccessResponse.consultationRequestId
      )

      directConsultationRequest.value =
        resolveDirectConsultation(callAccessResponse.consultationRequestId) ??
        (await getDirectConsultationById(callAccessResponse.consultationRequestId))

      callAccess.value = access
      persistActiveCallId(callAccessResponse.consultationRequestId, 'direct')
    } catch (err) {
      notifyCallError(err, 'Failed to join the call with the share code')
    } finally {
      isJoiningCall.value = false
    }
  }

  async function endCall(consultationRequestId: string): Promise<void> {
    try {
      await CallService.endCall(consultationRequestId)
      callAccess.value = undefined
      clearActiveCallId()
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to end the call'
      })
    }
  }

  function leaveCall() {
    callAccess.value = undefined
    clearActiveCallId()
  }

  async function joinScheduledConsultationCallById(
    consultationId: string,
    shareCode: string | undefined
  ): Promise<void> {
    isJoiningCall.value = true
    try {
      let scheduledCallAccess: JoinScheduledConsultationRequestWithShareCode200Response

      if (shareCode) {
        scheduledCallAccess = await CallService.joinScheduledCallWithShareCode(shareCode)
      } else {
        scheduledCallAccess = await CallService.joinScheduledCall(consultationId)
        scheduledConsultation.value = resolveScheduledConsultation(
          scheduledCallAccess.scheduledConsultationRequestId
        )
      }
      callAccess.value = toScheduledCallAccess(scheduledCallAccess)

      persistActiveCallId(consultationId, 'scheduled')

      await Promise.all([
        refreshOutgoingConsultationRequestList(),
        refreshIncomingConsultationRequestList(),
        refreshScheduledConsultationById(consultationId)
      ])
    } catch (error) {
      notifyCallError(error, 'Failed to scheduled join the call')
      throw error
    } finally {
      isJoiningCall.value = false
    }
  }

  function notifyCallError(error: unknown, fallbackMessage: string) {
    noticeStore.add({
      type: NoticeType.ERROR,
      message:
        error instanceof UnsupportedCallProviderError
          ? UNSUPPORTED_PROVIDER_MESSAGE
          : fallbackMessage
    })
  }

  function resolveDirectConsultation(requestId: string): ConsultationRequestDTO | undefined {
    return (
      getIncomingDirectConsultationById(requestId) ?? getOutgoingDirectConsultationById(requestId)
    )
  }

  function resolveScheduledConsultation(requestId: string): ScheduledConsultationDTO | null {
    return (
      getIncomingScheduledConsultationById(requestId) ??
      getOutgoingScheduledConsultationById(requestId)
    )
  }

  function persistActiveCallId(consultationId: string, type: CallType) {
    sessionStorage.setItem(ACTIVE_CALL_ID_KEY, consultationId)
    sessionStorage.setItem(ACTIVE_CALL_TYPE_KEY, type)
  }

  function clearActiveCallId() {
    sessionStorage.removeItem(ACTIVE_CALL_ID_KEY)
    sessionStorage.removeItem(ACTIVE_CALL_TYPE_KEY)
  }

  async function tryRestoreActiveCall(): Promise<void> {
    const activeCallId = sessionStorage.getItem(ACTIVE_CALL_ID_KEY)
    const activeCallType = sessionStorage.getItem(ACTIVE_CALL_TYPE_KEY)

    if (!activeCallId) return

    try {
      if (activeCallType === 'direct') {
        await joinCall(activeCallId)
      } else if (activeCallType === 'scheduled') {
        await joinScheduledConsultationCallById(activeCallId, undefined)
      }

      if (!callAccess.value) {
        clearActiveCallId()
      }
    } catch (err) {
      console.warn('Failed to auto-restore active call', err)
      clearActiveCallId()
    }
  }
  return {
    isInCall,
    callProvider,
    isAkCall,
    isWebexCall,
    webexCallAccess,
    roomId,
    accessToken,
    callAccess,
    callConsultationRequestId,
    isInDirectCall,
    isInScheduledCall,
    isJoiningCall,
    consultationShareCode,
    scheduledConsultationShareCode,
    scheduledConsultation,
    directConsultationRequest,
    startCall,
    joinCall,
    endCall,
    leaveCall,
    getConsultationShareCode,
    getScheduledConsultationShareCode,
    joinCallWithConsultationShareCode,
    joinScheduledConsultationCallById,
    tryRestoreActiveCall
  }
})
